import type {
  CustomFieldValidator,
  CustomFieldValidators,
  FieldDescriptor,
  FieldError,
  FieldErrorMap,
  ValidationRule,
} from './MasterDocument.types';

export type { CustomFieldValidator, CustomFieldValidators };

/**
 * Motor de validación en cliente para `FieldDescriptor.validations`.
 *
 * Hasta ahora `validations` era **configuración muerta**: el tipo la declaraba, los
 * templates la escribían (231 reglas en 20 templates solo en el módulo comercial) y
 * ningún punto del kit la leía. El canal `error` que pinta `FieldRenderer` lo llenaba
 * únicamente el servidor, al guardar, así que el `message` que cada regla trae escrito
 * no llegaba nunca a la pantalla.
 *
 * Este módulo es deliberadamente **puro**: sin React, sin estado, sin i18n. Recibe el
 * descriptor y el valor y devuelve el primer incumplimiento. Eso lo hace testeable de
 * verdad y deja que cada contenedor decida CUÁNDO enseñarlo.
 *
 * @see FieldRenderer — lo consume y decide el momento (tras tocar el campo)
 */

/**
 * Valor "vacío" a efectos de validación. Alineado con el criterio que ya usaba
 * `FieldRenderer` para el borde rojo de requerido-vacío, para que el borde y el
 * mensaje no puedan discrepar.
 */
export function isEmptyValue(value: unknown): boolean {
  return (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '') ||
    (Array.isArray(value) && value.length === 0)
  );
}

function asNumber(v: unknown): number | undefined {
  if (typeof v === 'number') return Number.isFinite(v) ? v : undefined;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

/**
 * ¿Incumple el valor esta regla concreta?
 *
 * Contrato deliberado: **sobre un valor vacío solo puede fallar `required`**. Apilar
 * "demasiado corto" o "menor que el mínimo" encima de un campo opcional en blanco es
 * ruido, y en un campo obligatorio taparía el mensaje que de verdad importa.
 *
 * Una regla que este motor NO sabe evaluar (un `custom` sin validador registrado, un
 * `pattern` con una expresión inválida, un `min` sin número) se considera **cumplida**:
 * el kit no puede bloquear al usuario por una regla que no entiende.
 */
function incumple(
  rule: ValidationRule,
  value: unknown,
  fieldDef: FieldDescriptor,
  customValidators?: CustomFieldValidators,
  values?: Record<string, unknown>,
): boolean {
  const vacio = isEmptyValue(value);

  if (rule.type === 'required') return vacio;
  if (vacio) return false;

  switch (rule.type) {
    case 'min': {
      const cota = asNumber(rule.value);
      const n = asNumber(value);
      return cota !== undefined && n !== undefined && n < cota;
    }
    case 'max': {
      const cota = asNumber(rule.value);
      const n = asNumber(value);
      return cota !== undefined && n !== undefined && n > cota;
    }
    case 'minLength': {
      const cota = asNumber(rule.value);
      return cota !== undefined && String(value).length < cota;
    }
    case 'maxLength': {
      const cota = asNumber(rule.value);
      return cota !== undefined && String(value).length > cota;
    }
    case 'pattern': {
      if (typeof rule.value !== 'string' && !(rule.value instanceof RegExp)) return false;
      try {
        const re = rule.value instanceof RegExp ? rule.value : new RegExp(rule.value);
        return !re.test(String(value));
      } catch {
        // Expresión inválida en el template: no es culpa del usuario.
        return false;
      }
    }
    case 'custom': {
      const id = typeof rule.value === 'string' ? rule.value : undefined;
      const fn = id ? customValidators?.[id] : undefined;
      if (!fn) return false;
      try {
        return !fn(value, { fieldDef, values });
      } catch {
        return false;
      }
    }
    default:
      return false;
  }
}

/**
 * Primer incumplimiento del campo, o `null` si cumple todo.
 *
 * Los `severity: 'error'` ganan a los `warning` aunque aparezcan después en el array:
 * si un campo incumple las dos cosas, lo que hay que enseñar es el error.
 */
export function evaluateFieldValidations(
  fieldDef: FieldDescriptor,
  value: unknown,
  customValidators?: CustomFieldValidators,
  values?: Record<string, unknown>,
): FieldError | null {
  const reglas = fieldDef.validations;
  if (!reglas || reglas.length === 0) return null;

  let warning: FieldError | null = null;
  for (const rule of reglas) {
    if (!incumple(rule, value, fieldDef, customValidators, values)) continue;
    if (rule.severity === 'error') return { message: rule.message, severity: 'error' };
    warning ??= { message: rule.message, severity: 'warning' };
  }
  return warning;
}

/**
 * Evalúa un conjunto de campos contra sus valores. Pensado para que un contenedor
 * decida si puede guardar; el kit NO bloquea ningún botón por su cuenta.
 */
export function evaluateFields(
  fields: FieldDescriptor[],
  values: Record<string, unknown>,
  customValidators?: CustomFieldValidators,
): FieldErrorMap {
  const out: FieldErrorMap = {};
  for (const f of fields) {
    const e = evaluateFieldValidations(f, values[f.key], customValidators, values);
    if (e) out[f.key] = e;
  }
  return out;
}

/**
 * Campos que IMPIDEN guardar, indexados por key. Lo que devuelve encaja tal cual en el
 * canal `error` que ya pinta `FieldRenderer`, así que el mensaje del campo y cualquier
 * otra señal que se derive de este mapa —el badge de la pestaña, p.ej.— salen de la MISMA
 * computación y no pueden contradecirse (ADR-027 §C.3b).
 *
 * Honra las DOS formas de declarar obligatoriedad, que en los templates reales no
 * coinciden: el flag `required` del descriptor y la regla `{ type: 'required' }` de
 * `validations`. De los 87 campos de línea obligatorios del módulo comercial, 5 traen
 * SOLO el flag; un guardián que mirara únicamente `validations` los dejaría pasar
 * mientras el asterisco de su pestaña sí los cuenta.
 *
 * Solo devuelve incumplimientos de severidad `error`: un `warning` avisa, no bloquea.
 *
 * El llamador decide QUÉ campos entran. Debe pasar únicamente los que el usuario puede
 * resolver ahora mismo —visibles y editables en el estado actual—: un campo oculto por
 * condición, no editable por estado, sin el permiso de edición (que llega con
 * `editableOn: []`) o calculado no puede bloquear a nadie, porque no habría forma de
 * desbloquearlo.
 */
export function evaluateBlockingFields(
  fields: FieldDescriptor[],
  values: Record<string, unknown>,
  options: { requiredFallbackMessage: string; customValidators?: CustomFieldValidators },
): FieldErrorMap {
  const out: FieldErrorMap = {};
  for (const f of fields) {
    const value = values[f.key];

    if (f.required && isEmptyValue(value)) {
      // El mensaje del template si lo declara; si solo trae el flag, el genérico del kit.
      const regla = f.validations?.find((r) => r.type === 'required' && r.severity === 'error');
      out[f.key] = {
        message: regla?.message ?? options.requiredFallbackMessage,
        severity: 'error',
      };
      continue;
    }

    const e = evaluateFieldValidations(f, value, options.customValidators, values);
    if (e && e.severity === 'error') out[f.key] = e;
  }
  return out;
}
